import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { differenceInDays } from 'date-fns';

export type UrgencyLevel = 'low' | 'medium' | 'high' | 'critical';

export const jurisdictionImages: Record<string, string> = {
  'Australia':             '/images/aus.jpg',
  'New Zealand':           '/images/nz.jpg',
  'Samoa':                 '/images/samoa.jpg',
  'Cayman Islands':        '/images/ky.jpg',
  'Bermuda':               '/images/bm.jpg',
  'British Virgin Islands': '/images/vg.jpg',
  'Gibraltar':             '/images/gi.jpg',
  'Guernsey':              '/images/guernsey.jpg',
  'Jersey':                '/images/jersey.jpg',
  'Isle of Man':           '/images/im.jpg',
  'Luxembourg':            '/images/lu.jpg',
  'Ireland':               '/images/ie.jpg',
  'Singapore':             '/images/sg.jpg',
  'Hong Kong':             '/images/hk.jpg',
  'United Kingdom':        '/images/uk.jpg',
  'United States':         '/images/us.jpg',
  'European Union':        '/images/eu.jpg',
  'Global':                '/images/global.jpg',
};

const DEFAULT_CARD_IMAGE = '/images/default.jpg';

export function getCardImage(imageUrl: string | null | undefined, jurisdiction: string): string {
  return imageUrl ?? jurisdictionImages[jurisdiction] ?? DEFAULT_CARD_IMAGE;
}

/** Merge Tailwind CSS classes without conflicts. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Compute urgency level from a due-date string. */
export function calculateUrgency(dueDate: string): UrgencyLevel {
  try {
    if (!dueDate) return 'low';

    let dateObj: Date;
    if (dueDate.includes('T')) {
      dateObj = new Date(dueDate);
    } else if (dueDate.includes('-') && dueDate.includes(':')) {
      dateObj = new Date(dueDate.replace(' ', 'T'));
    } else {
      dateObj = new Date(dueDate);
    }

    if (Number.isNaN(dateObj.getTime())) return 'low';

    const daysRemaining = differenceInDays(dateObj, new Date());

    if (daysRemaining <= 7) return 'critical';
    if (daysRemaining <= 14) return 'high';
    if (daysRemaining <= 30) return 'medium';
    return 'low';
  } catch {
    return 'low';
  }
}

const IMPACT_COLORS: Record<string, { chip: string; unselected: string; selected: string }> = {
  'high':   { chip: 'bg-red-100 text-red-800 border-red-200',     unselected: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',     selected: 'bg-red-600 text-white border-red-600' },
  'medium': { chip: 'bg-amber-100 text-amber-800 border-amber-200', unselected: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100', selected: 'bg-amber-500 text-white border-amber-500' },
  'low':    { chip: 'bg-green-100 text-green-800 border-green-200', unselected: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100', selected: 'bg-green-600 text-white border-green-600' },
};

const IMPACT_DEFAULT = { chip: 'bg-gray-100 text-gray-700 border-gray-200', unselected: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100', selected: 'bg-gray-600 text-white border-gray-600' };

/** Returns Tailwind classes for an impact-level chip (read-only display). */
export function impactChipClass(impactLevel: string | null | undefined): string {
  const base = 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize border';
  const colors = (impactLevel ? IMPACT_COLORS[impactLevel] : undefined) ?? IMPACT_DEFAULT;
  const noHover = colors.chip.split(' ').filter(c => !c.startsWith('hover:')).join(' ');
  return `${base} ${noHover}`;
}

/** Returns Tailwind classes for an impact-level filter button (toggle style). */
export function impactFilterClass(impactLevel: string, isSelected: boolean): string {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer select-none';
  const colors = IMPACT_COLORS[impactLevel] ?? IMPACT_DEFAULT;
  return `${base} ${isSelected ? colors.selected : colors.unselected}`;
}

/** Format an ISO date string to a locale-aware readable date. */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Format an ISO date string to YYYY-MM-DD. */
export function formatDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().split('T')[0];
}

